import debounce from "lodash/debounce";
import { ChevronRight } from "lucide-react";
import PropTypes from "prop-types";
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import { usePreferences } from "../../hooks/usePreferences";
import { useUserTemplate } from "../../hooks/useUserTemplate";
import { getTemplateName } from "../../services/supabaseUserUtils";
import { toSlug } from "../../utils";
import { ConcisenessSlider } from "../Templates/ConcisenessSlider";
import AddDocumentTypeModal from "./AddDocumentTypeModal";
import AddVisitTypeModal from "./AddVisitTypeModal";
import CreateDocumentTypeModal from "./CreateDocumentTypeModal";
import CreateVisitTypeModal from "./CreateVisitTypeModal";
import SelectTemplateModal from "./SelectTemplateModal";
import SelectVisitTypeModal from "./SelectVisitTypeModal";

const FIELD_SUCCESS_MESSAGES = {
  generalInstructions: "General instructions saved successfully",
  preferredTerminology: "Preferred terminology saved successfully",
};

function getSuccessMessage(field) {
  return FIELD_SUCCESS_MESSAGES[field] ?? "Changes saved successfully";
}

const Breadcrumb = ({ items }) => (
  <nav className="flex" aria-label="Breadcrumb">
    <ol className="flex items-center space-x-2">
      {items.map((item, index) => (
        <li key={item.href || item.label} className="flex items-center">
          {index > 0 && <ChevronRight className="mx-2 h-4 w-4 text-text-disabled" />}
          {item.href ? (
            <Link
              to={item.href}
              className="text-sm font-medium text-muted-foreground hover:text-foreground"
            >
              {item.label}
            </Link>
          ) : (
            <span className="text-sm font-medium text-foreground">{item.label}</span>
          )}
        </li>
      ))}
    </ol>
  </nav>
);

Breadcrumb.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      href: PropTypes.string,
    })
  ).isRequired,
};

const ReviewPreferences = () => {
  const { documentType, visitType, templateId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    loading,
    documentTypes,
    visitTypes,
    visitDetails,
    fetchDocumentTypes,
    fetchVisitTypes,
    fetchVisitDetails,
    createDocumentType,
    fetchCategories,
    createVisitType,
    createVisitDetail,
    updateVisitDetail,
    updateDocumentType,
    fetchDocumentType,
    fetchVisitType,
    updateVisitType,
  } = usePreferences();

  const { userTemplate: template, updateUserTemplate: handleStyleChange } = useUserTemplate({
    templateId,
  });
  const [userTemplateInputs, setUserTemplateInputs] = useState({
    generalInstructions: "",
    preferredTerminology: "",
  });

  const [templateName, setTemplateName] = useState("");
  const [expandedDetailId, setExpandedDetailId] = useState(null);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [categories, setCategories] = useState([]);
  const [showAddVisitTypeModal, setShowAddVisitTypeModal] = useState(false);
  const [showVisitTemplateModal, setShowVisitTemplateModal] = useState(false);
  const [showCreateVisitTypeModal, setShowCreateVisitTypeModal] = useState(false);
  const [documentFormData, setDocumentFormData] = useState({
    name: "",
    category: "",
    description: "",
  });

  const [visitFormData, setVisitFormData] = useState({
    name: "",
    description: "",
    details: {},
  });

  const [unsavedVisitDetails, setUnsavedVisitDetails] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    if (templateId) {
      fetchDocumentTypes(templateId);
    }
  }, [templateId]);

  // Load visit types when document type is selected
  useEffect(() => {
    if (documentType && templateId) {
      fetchVisitTypes(documentType, templateId);
    }
  }, [documentType, templateId]);

  // Load visit details when visit type is selected
  useEffect(() => {
    if (visitType) {
      fetchVisitDetails(visitType, templateId);
    }
  }, [visitType]);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const data = await fetchCategories();
        setCategories(data);
      } catch (error) {
        console.error("Error loading categories:", error);
      }
    };

    loadCategories();
  }, []);

  // Update form data when document type changes
  useEffect(() => {
    if (documentType && templateId) {
      const loadDocumentType = async () => {
        try {
          const docTypeData = await fetchDocumentType(templateId, documentType);
          setDocumentFormData({
            name: docTypeData.name,
            category: docTypeData.category,
            description: docTypeData.description,
          });
        } catch (error) {
          console.error("Error loading document type:", error);
        }
      };
      loadDocumentType();
    }
  }, [documentType, templateId]);

  useEffect(() => {
    if (visitType && templateId) {
      const loadVisitType = async () => {
        try {
          const visitTypeData = await fetchVisitType(templateId, visitType);
          setVisitFormData({
            name: visitTypeData.data.name,
            description: visitTypeData.data.description,
            details: visitTypeData.data.details || {},
          });
        } catch (error) {
          console.error("Error loading visit type:", error);
        }
      };
      loadVisitType();
    }
  }, [visitType, templateId]);

  // Add effect to fetch template name
  useEffect(() => {
    const loadTemplateName = async () => {
      if (templateId) {
        const name = await getTemplateName(templateId);
        setTemplateName(name || "Review Preferences");
      }
    };
    loadTemplateName();
  }, [templateId]);

  useEffect(() => {
    setUserTemplateInputs({
      generalInstructions: template?.generalInstructions || "",
      preferredTerminology: template?.preferredTerminology || "",
    });
  }, [template?.generalInstructions, template?.preferredTerminology]);

  const handleVisitDetailChange = (detailId, changes) => {
    setUnsavedVisitDetails((prev) => ({
      ...prev,
      [detailId]: {
        ...(prev[detailId] || {}),
        ...changes,
      },
    }));
    setHasUnsavedChanges(true);
  };

  const handleSaveChanges = async () => {
    try {
      if (visitType) {
        // Save visit type metadata
        const visitData = {
          name: visitFormData.name,
          description: visitFormData.description,
          details: visitFormData.details || {},
        };
        await updateVisitType(templateId, visitType, visitData);

        // Save all visit details changes
        for (const [detailId, changes] of Object.entries(unsavedVisitDetails)) {
          const currentDetail = visitDetails.find((d) => d.detail_id === detailId);
          const updatedDetail = {
            ...currentDetail,
            ...changes,
            required: currentDetail?.required || [],
            promptTemplate: currentDetail?.promptTemplate || "",
          };
          await updateVisitDetail(templateId, visitType, detailId, updatedDetail);
        }

        // Clear unsaved changes
        setUnsavedVisitDetails({});
        setHasUnsavedChanges(false);

        // Refresh the data
        await fetchVisitDetails(visitType, templateId);
        toast.success("Changes saved successfully");
      } else if (documentType) {
        await handleUpdateDocumentType();
      }
    } catch (error) {
      console.error("Error saving changes:", error);
      toast.error("Failed to save changes");
    }
  };

  const getBreadcrumbItems = () => {
    const items = [
      {
        label: templateName || "Review Preferences",
        href: templateId ? `/templates/${templateId}` : "/preferences",
      },
    ];

    if (documentType) {
      const docTypeObj = documentTypes.find((d) => d.document_type_id === documentType);
      items.push({
        label: docTypeObj?.name || documentType,
        href:
          documentType && !visitType
            ? null
            : `/templates/${templateId}/document-types/${documentType}`,
      });
    }

    if (visitType) {
      const visitTypeObj = visitTypes.find((v) => v.visit_type_id === visitType);
      items.push({
        label: visitTypeObj?.name || visitType,
        href: null,
      });
    }

    return items;
  };

  const handleAddDocumentType = () => {
    setShowAddModal(true);
  };

  const handleSelectTemplate = () => {
    setShowAddModal(false);
    setShowTemplateModal(true);
  };

  const handleCreateNew = () => {
    setShowAddModal(false);
    setShowCreateModal(true);
  };

  const handleTemplateSelected = async (template) => {
    try {
      // Generate a new unique ID for this document type
      const newDocTypeId = `${template.document_type_id}-${Date.now()}`;

      // Create new document type from template, passing the example_doc_type_id as a query parameter
      await createDocumentType(
        templateId,
        newDocTypeId,
        {
          name: template.name,
          category: template.category,
          description: template.description,
        },
        template.document_type_id // Pass as example_doc_type_id query parameter
      );

      // Close modal and refresh list
      setShowTemplateModal(false);
      await fetchDocumentTypes(templateId);

      // Navigate to the new document type
      navigate(`/templates/${templateId}/document-types/${newDocTypeId}`, {
        state: { ...location.state },
      });
    } catch (error) {
      console.error("Error creating from template:", error);
      toast.error("Failed to create document type from template");
    }
  };

  const handleCreateSubmit = async (templateId, docType, docData) => {
    try {
      await createDocumentType(templateId, docType, docData);
      setShowCreateModal(false);
      await fetchDocumentTypes(templateId);

      // Update navigation to include templateId in the path
      navigate(`/templates/${templateId}/document-types/${docType}`, {
        state: { ...location.state }, // Preserve any existing state
      });
    } catch (error) {
      console.error("Error creating document type:", error);
    }
  };

  const handleAddVisitType = () => {
    setShowAddVisitTypeModal(true);
  };

  const handleSelectVisitTemplate = () => {
    setShowAddVisitTypeModal(false);
    setShowVisitTemplateModal(true);
  };

  const handleCreateNewVisitType = () => {
    setShowAddVisitTypeModal(false);
    setShowCreateVisitTypeModal(true);
  };

  const handleVisitTemplateSelected = async (template) => {
    try {
      // Generate a new unique ID for this visit type
      const newVisitTypeId = `${template.visit_type_id}-${Date.now()}`;

      // Create new visit type from template
      await createVisitType(
        templateId,
        newVisitTypeId,
        {
          name: template.name,
          description: template.description,
          details: {}, // Details will be copied from example
        },
        documentType, // Current document type ID
        template.visit_type_id // Pass as example_visit_type_id
      );

      // Close modal and refresh list
      setShowVisitTemplateModal(false);
      await fetchVisitTypes(documentType, templateId);

      // Navigate to the new visit type
      navigate(
        `/templates/${templateId}/document-types/${documentType}/visit-types/${newVisitTypeId}`,
        {
          state: { ...location.state },
        }
      );
    } catch (error) {
      console.error("Error creating from template:", error);
      toast.error("Failed to create visit type from template");
    }
  };

  const handleCreateVisitTypeSubmit = async (formData) => {
    try {
      const visitTypeId = toSlug(formData.name);
      const visitData = {
        name: formData.name,
        description: formData.description,
        details: {}, // Start with empty details
      };

      await createVisitType(templateId, visitTypeId, visitData, documentType);
      setShowCreateVisitTypeModal(false);
      await fetchVisitTypes(documentType, templateId);

      // Navigate to the new visit type
      navigate(
        `/templates/${templateId}/document-types/${documentType}/visit-types/${visitTypeId}`,
        {
          state: { ...location.state },
        }
      );
    } catch (error) {
      console.error("Error creating visit type:", error);
      toast.error("Failed to create visit type");
    }
  };

  const handleAddVisitDetail = async () => {
    try {
      // Create a new detail with a default name
      const detailName = `new-detail-${visitDetails.length + 1}`;

      await createVisitDetail(templateId, visitType, detailName);
      await fetchVisitDetails(visitType, templateId);
      toast.success("Visit detail added successfully");
    } catch (error) {
      console.error("Error creating visit detail:", error);
      toast.error("Failed to add visit detail");
    }
  };

  const handleUpdateDocumentType = async () => {
    try {
      const currentDocType = documentTypes.find((d) => d.document_type_id === documentType);
      const docData = {
        name: documentFormData.name,
        category: documentFormData.category,
        description: documentFormData.description,
        visitTypes: currentDocType?.visitTypes || [],
        required: currentDocType?.required || ["date", "provider", "facility"],
      };

      await updateDocumentType(templateId, documentType, docData);
      await fetchDocumentTypes(templateId);
    } catch (error) {
      console.error("Error updating document type:", error);
      toast.error("Failed to update document type");
    }
  };

  const handleUserTemplateChange = async (field, value) => {
    try {
      await handleStyleChange(field, value);
      const message = getSuccessMessage(field);
      toast.success(message);
    } catch (error) {
      console.error("Error updating user template:", error);
      toast.error("Failed to update user template");
    }
  };
  const debouncedHandleUpdateUserTemplate = useMemo(
    () => debounce(handleUserTemplateChange, 500),
    []
  );

  useEffect(
    () => () => {
      debouncedHandleUpdateUserTemplate.flush();
    },
    [debouncedHandleUpdateUserTemplate]
  );

  const handleUpdateUserTemplate = (field, value) => {
    setUserTemplateInputs((prev) => ({ ...prev, [field]: value }));
    debouncedHandleUpdateUserTemplate(field, value);
  };

  // const handleUpdateVisitType = async () => {
  //   try {
  //     const visitData = {
  //       name: visitFormData.name,
  //       description: visitFormData.description,
  //       details: visitFormData.details || {},
  //     };

  //     await updateVisitType(templateId, visitType, visitData);
  //     toast.success("Visit type updated successfully");
  //   } catch (error) {
  //     console.error("Error updating visit type:", error);
  //     toast.error("Failed to update visit type");
  //   }
  // };

  const renderMainPreferences = () => {
    return (
      <div className="space-y-8">
        <div>
          <h2 className="mb-6 text-lg font-medium text-foreground">Report Style</h2>

          <div className="space-y-6">
            <div>
              <label className="mb-2 block text-sm font-medium text-muted-foreground">
                General Instructions
              </label>
              <textarea
                value={userTemplateInputs.generalInstructions}
                onChange={(e) => handleUpdateUserTemplate("generalInstructions", e.target.value)}
                className="w-full rounded-md border-input shadow-sm focus-visible:shadow-focus"
                placeholder="Enter any general instructions for report generation..."
                rows={4}
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-muted-foreground">
                Default Concision
              </label>
              {templateId ? (
                <ConcisenessSlider templateId={templateId} showExample={true} />
              ) : (
                <div aria-busy="true" aria-label="Loading conciseness controls">
                  <Skeleton height={20} borderRadius={8} containerClassName="block w-full" />
                </div>
              )}
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-muted-foreground">
                Preferred Terminology
              </label>
              <textarea
                value={userTemplateInputs.preferredTerminology}
                onChange={(e) => handleUpdateUserTemplate("preferredTerminology", e.target.value)}
                className="w-full rounded-md border-input shadow-sm focus-visible:shadow-focus"
                placeholder="Enter preferred medical terms and abbreviations..."
                rows={4}
              />
            </div>
          </div>
        </div>

        <div>
          <h2 className="mb-4 text-lg font-medium text-foreground">Document Types</h2>
          <div className="space-y-2">
            {documentTypes.map((docType) => (
              <div
                key={docType.document_type_id}
                onClick={() =>
                  navigate(`/templates/${templateId}/document-types/${docType.document_type_id}`, {
                    state: {
                      ...location.state,
                      documentName: docType.name,
                    },
                  })
                }
                className="flex cursor-pointer items-center justify-between rounded-lg border border-border bg-card p-4 hover:bg-surface-subtle"
              >
                <span className="text-sm font-medium text-foreground">{docType.name}</span>
                <ChevronRight className="h-5 w-5 text-text-disabled" />
              </div>
            ))}
            <button
              onClick={handleAddDocumentType}
              className="w-full rounded-lg border border-input p-4 text-center text-sm text-muted-foreground hover:bg-surface-subtle"
            >
              + Add Document Type
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderDocumentTypePreferences = () => {
    // const currentDocType = documentTypes.find((d) => d.document_type_id === documentType);

    return (
      <div className="space-y-6">
        <div>
          <label className="mb-2 block text-sm font-medium text-muted-foreground">
            Document Type Name
          </label>
          <input
            type="text"
            value={documentFormData.name}
            onChange={(e) => setDocumentFormData({ ...documentFormData, name: e.target.value })}
            className="w-full rounded-md border-input shadow-sm focus-visible:shadow-focus"
          />
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium text-muted-foreground">Category</label>
          <select
            value={documentFormData.category}
            onChange={(e) => setDocumentFormData({ ...documentFormData, category: e.target.value })}
            className="w-full rounded-md border-input shadow-sm focus-visible:shadow-focus"
          >
            <option value="">Select Category</option>
            {categories.map((category) => (
              <option key={category.name} value={category.value}>
                {category.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium text-muted-foreground">
            Description
          </label>
          <textarea
            value={documentFormData.description}
            onChange={(e) =>
              setDocumentFormData({ ...documentFormData, description: e.target.value })
            }
            className="w-full rounded-md border-input shadow-sm focus-visible:shadow-focus"
            rows={4}
          />
        </div>

        <div className="flex justify-end">
          <button
            onClick={handleUpdateDocumentType}
            className="rounded-md border border-transparent bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-hover"
          >
            Save Changes
          </button>
        </div>

        <div>
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-medium text-foreground">Visit Types</h3>
            {visitTypes.length === 0 && (
              <span className="text-sm text-muted-foreground">
                No visit types defined for this document type
              </span>
            )}
          </div>
          <div className="space-y-2">
            {visitTypes.map((visitType) => (
              <div
                key={visitType.visit_type_id}
                onClick={() =>
                  navigate(
                    `/templates/${templateId}/document-types/${documentType}/visit-types/${visitType.visit_type_id}`,
                    { state: { ...location.state } }
                  )
                }
                className="flex cursor-pointer items-center justify-between rounded-lg border border-border bg-card p-4 hover:bg-surface-subtle"
              >
                <span className="text-sm font-medium text-foreground">{visitType.name}</span>
                <ChevronRight className="h-5 w-5 text-text-disabled" />
              </div>
            ))}
            <button
              onClick={handleAddVisitType}
              className="w-full rounded-lg border border-input p-4 text-center text-sm text-muted-foreground hover:bg-surface-subtle"
            >
              + Add Visit Type
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderVisitTypePreferences = () => {
    return (
      <div className="space-y-6">
        <div>
          <label className="mb-2 block text-sm font-medium text-muted-foreground">
            Visit Type Name
          </label>
          <input
            type="text"
            value={visitFormData.name}
            onChange={(e) => {
              setVisitFormData({ ...visitFormData, name: e.target.value });
              setHasUnsavedChanges(true);
            }}
            className="w-full rounded-md border-input shadow-sm focus-visible:shadow-focus"
          />
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium text-muted-foreground">
            Description
          </label>
          <textarea
            value={visitFormData.description}
            onChange={(e) => {
              setVisitFormData({ ...visitFormData, description: e.target.value });
              setHasUnsavedChanges(true);
            }}
            className="w-full rounded-md border-input shadow-sm focus-visible:shadow-focus"
            rows={4}
          />
        </div>

        <div>
          <h3 className="mb-4 text-lg font-medium text-foreground">Visit Details</h3>
          <div className="space-y-4">
            {visitDetails.map((detail) => (
              <div
                key={detail.detail_id}
                className="overflow-hidden rounded-lg border border-border bg-card"
              >
                <div
                  onClick={() =>
                    setExpandedDetailId(
                      expandedDetailId === detail.detail_id ? null : detail.detail_id
                    )
                  }
                  className="flex cursor-pointer items-center justify-between p-4 hover:bg-surface-subtle"
                >
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-foreground">{detail.name}</h4>
                    <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">
                      {detail.description}
                    </p>
                  </div>
                  <ChevronRight
                    className={`h-5 w-5 transform text-text-disabled transition-transform duration-200 ${
                      expandedDetailId === detail.detail_id ? "rotate-90" : ""
                    }`}
                  />
                </div>

                {expandedDetailId === detail.detail_id && (
                  <div className="space-y-4 border-t border-border bg-surface-subtle p-4">
                    <div>
                      <label className="mb-2 block text-sm font-medium text-muted-foreground">
                        Name
                      </label>
                      <input
                        type="text"
                        defaultValue={detail.name}
                        onChange={(e) =>
                          handleVisitDetailChange(detail.detail_id, {
                            name: e.target.value,
                          })
                        }
                        className="w-full rounded-md border-input shadow-sm focus-visible:shadow-focus"
                      />
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-medium text-muted-foreground">
                        Description
                      </label>
                      <textarea
                        defaultValue={detail.description}
                        onChange={(e) =>
                          handleVisitDetailChange(detail.detail_id, {
                            description: e.target.value,
                          })
                        }
                        rows={2}
                        className="w-full rounded-md border-input shadow-sm focus-visible:shadow-focus"
                      />
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-medium text-muted-foreground">
                        Instructions
                      </label>
                      <textarea
                        defaultValue={detail.instructions}
                        onChange={(e) =>
                          handleVisitDetailChange(detail.detail_id, {
                            instructions: e.target.value,
                          })
                        }
                        rows={3}
                        className="w-full rounded-md border-input shadow-sm focus-visible:shadow-focus"
                      />
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-medium text-muted-foreground">
                        Example
                      </label>
                      <textarea
                        defaultValue={detail.example}
                        onChange={(e) =>
                          handleVisitDetailChange(detail.detail_id, {
                            example: e.target.value,
                          })
                        }
                        rows={2}
                        className="w-full rounded-md border-input shadow-sm focus-visible:shadow-focus"
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
            <button
              onClick={handleAddVisitDetail}
              className="w-full rounded-lg border border-input p-4 text-center text-sm text-muted-foreground hover:bg-surface-subtle"
            >
              + Add Visit Detail
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="flex h-64 items-center justify-center">
          <div className="text-muted-foreground">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="container mx-auto px-4 py-6">
        <div className="mb-6 flex items-center justify-between">
          <Breadcrumb items={getBreadcrumbItems()} />
          {(visitType || documentType) && (
            <button
              onClick={handleSaveChanges}
              disabled={!hasUnsavedChanges}
              className={`rounded-md px-4 py-2 text-sm font-medium ${
                hasUnsavedChanges
                  ? "bg-primary text-white hover:bg-primary-hover"
                  : "cursor-not-allowed bg-border-strong text-muted-foreground"
              }`}
            >
              Save Changes
            </button>
          )}
        </div>

        <div className="rounded-lg bg-card shadow">
          <div className="p-6">
            {visitType
              ? renderVisitTypePreferences()
              : documentType
                ? renderDocumentTypePreferences()
                : renderMainPreferences()}
          </div>
        </div>
      </div>

      <AddDocumentTypeModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSelectTemplate={handleSelectTemplate}
        onCreateNew={handleCreateNew}
      />

      <SelectTemplateModal
        isOpen={showTemplateModal}
        onClose={() => setShowTemplateModal(false)}
        onSelect={handleTemplateSelected}
        templateId={templateId}
      />

      <CreateDocumentTypeModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateSubmit}
        categories={categories}
        templateId={templateId}
      />

      <AddVisitTypeModal
        isOpen={showAddVisitTypeModal}
        onClose={() => setShowAddVisitTypeModal(false)}
        onSelectTemplate={handleSelectVisitTemplate}
        onCreateNew={handleCreateNewVisitType}
      />

      <SelectVisitTypeModal
        isOpen={showVisitTemplateModal}
        onClose={() => setShowVisitTemplateModal(false)}
        onSelect={handleVisitTemplateSelected}
        documentTypeId={documentType}
        templateId={templateId}
      />

      <CreateVisitTypeModal
        isOpen={showCreateVisitTypeModal}
        onClose={() => setShowCreateVisitTypeModal(false)}
        onSubmit={handleCreateVisitTypeSubmit}
      />
    </>
  );
};

export default ReviewPreferences;
